import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Crown, CheckCircle, XCircle, ArrowRight, Music, Upload, DollarSign, Headphones } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LicensingPage = () => {
  const { t } = useLanguage();
  const lp = t('licensingPage') || {};
  const cards = lp.cards || [];
  const basicFeatures = lp.basicFeatures || [];
  const basicLimitations = lp.basicLimitations || [];
  const exclusiveFeatures = lp.exclusiveFeatures || [];
  const steps = lp.steps || [];
  const faqs = lp.faqs || [];
  const cardIcons = [Upload, DollarSign, Music, Headphones];

  return (
    <div className="min-h-screen pt-24" data-testid="licensing-page">
      <div className="container-custom py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white mb-8" data-testid="licensing-back-link">
          <ArrowLeft className="w-4 h-4" />{t('legal.backToHome')}
        </Link>

        {/* Hero */}
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E8720C] mb-4">{lp.overline}</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="licensing-title">
            {lp.heroTitle} <span className="gold-text">{lp.heroTitleHighlight}</span>
          </h1>
          <p className="text-base md:text-lg text-white/50 max-w-2xl mx-auto">{lp.heroSubtitle}</p>
        </div>

        {/* What You Can Do */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-8 text-center" data-testid="licensing-what-you-can-do">{lp.whatYouCanDo}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cards.map((item, i) => {
              const Icon = cardIcons[i] || Music;
              return (
                <div key={i} className="glass-card p-6 text-center hover:-translate-y-1 transition-transform">
                  <div className="w-14 h-14 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-white/50">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* License Comparison */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-8 text-center">{lp.comparisonTitle}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Basic */}
            <div className="glass-card p-8 border border-green-500/20" data-testid="basic-license-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-green-500/10 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{lp.basicName}</h3>
                  <p className="text-sm text-green-400">{lp.basicIncluded}</p>
                </div>
              </div>
              <p className="text-white/50 text-sm mb-6">{lp.basicDesc}</p>
              <div className="space-y-3 mb-6">
                {basicFeatures.map((text, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                    <span className="text-sm text-white/70">{text}</span>
                  </div>
                ))}
                {basicLimitations.map((text, i) => (
                  <div key={`lim-${i}`} className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-white/30 shrink-0" />
                    <span className="text-sm text-white/40">{text}</span>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t border-white/10">
                <p className="text-sm text-white/40">{lp.startingFrom}</p>
                <p className="text-3xl font-bold gold-text">$3.00 - $10.00</p>
              </div>
            </div>

            {/* Exclusive */}
            <div className="glass-card p-8 border border-[#E8720C]/30 relative overflow-hidden" data-testid="exclusive-license-card">
              <div className="absolute top-0 right-0 px-4 py-1 bg-[#E8720C] text-black text-xs font-bold rounded-bl-xl">{lp.exclusiveLabel}</div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-[#E8720C]/10 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-[#E8720C]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{lp.exclusiveName}</h3>
                  <p className="text-sm text-[#E8720C]">{lp.exclusiveIncluded}</p>
                </div>
              </div>
              <p className="text-white/50 text-sm mb-6">{lp.exclusiveDesc}</p>
              <div className="space-y-3 mb-6">
                {exclusiveFeatures.map((text, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#E8720C] shrink-0" />
                    <span className="text-sm text-white/70">{text}</span>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t border-white/10">
                <p className="text-sm text-white/40">{lp.startingFrom}</p>
                <p className="text-3xl font-bold gold-text">$99.99</p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Steps */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-8 text-center">{lp.howTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {steps.map((item, i) => (
              <div key={i} className="glass-card p-5 text-center relative">
                <div className="w-10 h-10 rounded-full bg-[#E8720C] flex items-center justify-center mx-auto mb-3">
                  <span className="text-sm font-bold text-black">{i + 1}</span>
                </div>
                <h4 className="font-semibold text-sm mb-1">{item.title}</h4>
                <p className="text-xs text-white/50">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-8 text-center">{lp.faqTitle}</h2>
          <div className="space-y-4">
            {faqs.map((item, i) => (
              <div key={i} className="glass-card p-6" data-testid={`licensing-faq-${i}`}>
                <h4 className="font-semibold mb-2">{item.q}</h4>
                <p className="text-sm text-white/50">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="glass-card p-8 md:p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#E8720C]/5 via-transparent to-transparent" />
          <div className="relative">
            <h2 className="text-3xl font-bold mb-4">{lp.ctaTitle}</h2>
            <p className="text-white/50 mb-6 max-w-lg mx-auto">{lp.ctaDesc}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/catalog" className="btn-gold" data-testid="licensing-browse-catalog">
                {lp.browseCatalog} <ArrowRight className="w-4 h-4 inline ml-1" />
              </Link>
              <Link to="/packs" className="btn-secondary" data-testid="licensing-view-packs">
                {lp.viewPacks}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicensingPage;
